import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StateService, Transition } from '@uirouter/core';
import { MonitoredTrain, TrainTypeConfiguration } from '../../models/monitored-train';
import { BehaviorSubject, Subscription, merge, of, Observable, interval } from 'rxjs';
import { switchMap, map, tap, takeWhile } from 'rxjs/operators';
import { FaultMonitoringDataService } from '../../services/fault-monitoring-data.service';
import { MonitoringBehaviorSubjectService } from '../../services/monitoring-behavior-subject.service';
import { MonitoringAlarm } from '../../models/monitoring-alarm';
import { TrainSyncData } from '../../models/train-sync-data';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ProjectConfiguration } from '../../../project/models/project-configuration';
import { ProjectConfigurationBehaviorSubjectService } from '../../../project/services/project-configuration-behavior-subject.service';
import { TrainEvent } from '../../models/trainEvent';
import { ConfigurationDataService } from '../../../configurations/services/configuration-data.service';
import { UserProfileService } from '../../../users/services/user-profile.service';
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { ReferenceArea } from '../../../reference-areas/models/reference-area';
import {Incident} from '../../../incidents/models/incident';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-monitored-train',
  template: require('./monitored-train.component.html'),
  styles: [require('./monitored-train.component.sass')]
})
export class MonitoredTrainComponent implements OnInit, OnDestroy {

  projectId: number;
  trainId: number;
  activeTab: string;

  currentTrain: MonitoredTrain | null;
  currentTrainAlarms: MonitoringAlarm[] | null;

  trainSyncData$: Observable<TrainSyncData[] | null>;
  events$ : Observable<TrainEvent[] | null>;

  trainEntities: any;
  trainOfficialIds: any[];
  selectedOfficialId: number | null ;

  private readonly refreshMonitoredTrain$ = new BehaviorSubject(undefined);
  monitoredTrainsSubscription: Subscription;

  monitoredCurrentTrainSubscription: Subscription;

  currentTrainSubscription: Subscription;

  changeSubscription: Subscription;

  projectConfigurationSubscription: Subscription;

  projectConfiguration: ProjectConfiguration;

  defaultChartTemplate: any;

  currentTrainTypeConfiguration: TrainTypeConfiguration;

  private readonly refreshReferenceAreas$ = new BehaviorSubject(undefined);
  referenceAreas$: Observable<ReferenceArea[]>;

  languageChangeSubscription: Subscription;
  externalWebsites: any[] | undefined;

  constructor(
    public $state: StateService,
    private trans: Transition,
    private faultMonitoringDataService: FaultMonitoringDataService,
    private monitoringBehaviorSubjectService: MonitoringBehaviorSubjectService,
    private projectConfigurationBehaviorSubjectService: ProjectConfigurationBehaviorSubjectService,
    private messageService: MessageService,
    private translate: TranslateService,
    public dialogService: DialogService,
    private configurationDataService: ConfigurationDataService,
    public userProfileService: UserProfileService,
    @Inject('MonitoringStoreService') private monitoringStoreService: any,
    @Inject('ConfigurationStoreService') private configurationStoreService: any,
    @Inject('TimeZoneFactory') private timeZoneFactory: any,
    private referenceAreaDataService: ReferenceAreaDataService,
    private cd: ChangeDetectorRef
  ) {
    this.activeTab = this.trans.params().tab || 'summary';
    this.trainId = this.trans.params().trainId;
    this.projectId = this.trans.params().projectId;
    if (!this.trainId) {
      this.$state.go('monitoring', {
        projectId: this.projectId
      });
    } else {
      this.monitoredTrainsSubscription = this.refreshMonitoredTrain$.pipe(
        switchMap(() => merge(
          of(this.currentTrain? [{
            ...this.currentTrain
          }] : null),
          this.faultMonitoringDataService.getAllTrains(this.projectId))
      )).subscribe(monitoredTrains => {
        if (monitoredTrains) {
          this.monitoringStoreService.setTrains(monitoredTrains);
        }
      })
    }

    this.referenceAreas$ = this.refreshReferenceAreas$.pipe(
      switchMap(() =>this.referenceAreaDataService.getReferenceAreas(this.projectId)));

    this.monitoredCurrentTrainSubscription =  merge(
      this.refreshMonitoredTrain$, 
      interval(environment.config.monitoring.refreshPeriod * 1000).pipe(takeWhile(() => {
        const monitoringFilters = localStorage.getItem('monitoring-filters-' + 0) || localStorage.getItem('monitoring-filters-' + this.projectId);
        const autoRefresh = monitoringFilters && JSON.parse(monitoringFilters).autoRefresh;
        return autoRefresh;
      }))).pipe(
      switchMap(() => this.faultMonitoringDataService.getTrain(this.projectId, this.trainId)))
      .subscribe(train => {
        if(train) {
          this.monitoringStoreService.setTrainByTrainId(this.trainId, train);
        } else {
          this.currentTrain = null;
        }
    })


    this.changeSubscription = this.monitoringBehaviorSubjectService.changeOccured.subscribe((data) => {
      if (data) {
        switch (data.action) {
          case 'current-train-updated':
          case 'trains-updated':
              this._getMonitoredTrainFromStore();
              this.processDataExternalWebsites();
          break;
        }
      }
      
    })
    this.projectConfigurationSubscription = this.projectConfigurationBehaviorSubjectService.changeOccured.subscribe(() => {
      this.projectConfiguration = this.configurationStoreService.getProjectConfiguration(this.projectId);
      this.processDataExternalWebsites();
    })

    this.trainSyncData$ = this.refreshMonitoredTrain$.pipe(
      switchMap(() => merge(
        of(null),
        this.faultMonitoringDataService.getTrainSynchronisation(this.projectId, this.trainId).pipe(
          map(events => {
            return events.map(event => {
                  event.date = this.timeZoneFactory.format(event.timestamp, 'YYYY-MM-DD HH:mm');
                  event.lastSuccess = event.latestSyncSuccess && event.bytesReceived === -1 ? this.timeZoneFactory.format(event.latestSyncSuccess, 'YYYY-MM-DD HH:mm') : null;
                  const now = Date.now();
                  if (event.bytesReceived === -1 && (now - event.latestSyncSuccess > 2 * 24 * 60 * 60000)) {
                      event.level = 5; // red
                  } else if (now - event.timestamp > 2 * 24 * 60 * 60000) {
                      event.level = 2; // yellow
                  } else {
                      event.level = 0; // green
                  }
                  return event;
              })
          }))
    )))


      this.events$ = this.refreshMonitoredTrain$.pipe(
        switchMap(() => merge(
          of(null),
          this.faultMonitoringDataService.getTrainEvents(this.projectId, this.trainId).pipe(
            map(events => events.map((event) => {
               return {
                 ...event,
                 message: event.message.replace(/_/g, " ").replace('mar3_', '')
               };
           }))
          )
      )))
      
      this.languageChangeSubscription = this.translate.onLangChange.subscribe((language) => {
        console.log(language)
        this.cd.detectChanges();
        this.processDataExternalWebsites(language.lang);
      })
   }

  ngOnInit(): void {
    if (!this.currentTrain || !this.trainEntities) {
      this.refreshMonitoredTrain$.next(undefined);
    }
    this.projectConfiguration = this.configurationStoreService.getProjectConfiguration(this.projectId);
    const trainDetailConfiguration = this.projectConfiguration && this.projectConfiguration["trainDetailConfiguration"];
    if(trainDetailConfiguration){
        this.defaultChartTemplate = trainDetailConfiguration["defaultChartTemplate"];
    }
    this.processDataExternalWebsites();
  }
  
  _getMonitoredTrainFromStore = async () => {
    const allTrains = this.monitoringStoreService.getUnfilteredTrains(this.projectId);
    this.trainEntities = allTrains.reduce((accumulator, train) => {
      accumulator[train.trainId] = train;
      return accumulator;
    }, {});
    this.trainOfficialIds = allTrains.map(train => {
    return train.trainId;
    });
    this.currentTrain = this.monitoringStoreService.getTrainByTrainId(this.projectId, this.trainId);
    if (this.currentTrain && this.currentTrain.projectId && !this.currentTrainTypeConfiguration) {
      const trainTypeConfigurations = await this.configurationDataService.getTrainTypeConfigurations(this.currentTrain.projectId, this.currentTrain.trainType).toPromise();
      this.currentTrainTypeConfiguration = trainTypeConfigurations[0];
    }

    this.selectedOfficialId = this.currentTrain && this.currentTrain.trainId;
    this.currentTrainAlarms = this.monitoringStoreService.getAlarms(this.projectId, this.trainId);
  }

  openTab = (tab: string) => {
    this.$state.go('monitoring-detail', {
      projectId: this.projectId,
      trainId: this.trainId,
      tab
    });
  }

  changeToTrain = (selectedOfficialId) => {
    this.trainId = this.trainEntities[selectedOfficialId] ? this.trainEntities[selectedOfficialId].trainId : null;
    if(this.trainId) {
      this.$state.go('monitoring-detail', {
        projectId: this.projectId, 
        trainId: this.trainId,
        tab: this.activeTab
      });
    }
  }
  changeToPreviousTrain = (selectedOfficialId) => {
    const index = this.trainOfficialIds.findIndex(trainId => {
      return trainId === selectedOfficialId;
    });
    if (index > 0) {
      this.changeToTrain(this.trainOfficialIds[index - 1]);
    }
  }

  changeToNextTrain = (selectedOfficialId) => {
    const index = this.trainOfficialIds.findIndex(trainId => {
      return trainId === selectedOfficialId;
    });
    if (index < this.trainOfficialIds.length - 1) {
      this.changeToTrain(this.trainOfficialIds[index + 1]);
    }
  }

  goToOverview = () => {
    this.$state.go('monitoring', {
      projectId: this.projectId
    });
  }

  reload = () => {
    this.refreshMonitoredTrain$.next(undefined);
    this.refreshReferenceAreas$.next(undefined);
  }

  goToGraph = () => {
    const hoursAgo = 4; // TODO put in configuration
    const currentTime = new Date();
    const startTime = new Date().setHours(currentTime.getHours() - hoursAgo);
    let routeData: any = {
      projectId: this.projectId,
      trainId: this.trainId,
      startDate: this.timeZoneFactory.format(startTime, 'YYYY-MM-DD-HH:mm'),
      endDate: this.timeZoneFactory.format(currentTime, 'YYYY-MM-DD-HH:mm')
    }
    if (this.defaultChartTemplate) {
      routeData = {
        ...routeData,
        templateConfigName: this.defaultChartTemplate
      }
    }
    this.$state.go('period-analysis.chart', routeData);
  }

  goToDiagnostics = () => {
    this.$state.go('diagtool', {
      projectId: this.projectId,
      trainId: this.trainId
    });
  }
  acknowledgeAlarm = async (alarms: MonitoringAlarm[]) => {
    alarms = alarms.filter(alarm => !alarm.acknowledged);
    if (alarms && alarms.length > 0) {
      const trainId = alarms[0].trainId;
      const projectId = alarms[0].projectId;
      const type = alarms[0].type;
      const eventIds = alarms.map(alarm => alarm.eventId);
      try {
        await this.faultMonitoringDataService.acknowledgeAlarms(projectId, trainId, type, eventIds);
      } catch (error) {
        this.messageService.add({
          severity:'error', 
          summary: this.translate.instant('I18N_MONITORING.ACK_ALARM'), 
          detail: this.translate.instant('I18N_MONITORING.ACK_ALARM_ERROR')
        });
      }

    }
    this.refreshMonitoredTrain$.next(undefined);
    this.monitoringBehaviorSubjectService.inform('current-train-updated', false);
  }

  investigateAlarm = (alarms: MonitoringAlarm[]) => {
    if (alarms && alarms.length > 0) {
      const zabbixURL = alarms[0].zabbixURL;
      if (zabbixURL) {
          // responseData.message is Zabbix Config Full URL -> http://vmtrainzabbix01.ovh.21net.com/api_jsonrpc.php
          const matchedResult = zabbixURL.match(/^https?:\/\/[^#?\/]+(:?\/zabbix)*/);
          if (matchedResult) {
            const zabbixBaseURL = matchedResult[0];
            for(let i = 0; i < alarms.length; i++) {
              window.open(zabbixBaseURL + '/tr_events.php?triggerid=' + alarms[i].triggerId + '&eventid=' + alarms[i].eventId);
            }
          } else {
            this.messageService.add({
              severity:'error', 
              summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
              detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL')
            });
          }
  
      } else {
          this.messageService.add({
            severity:'error', 
            summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
            detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL_SELECTED_TRAIN')
          });
      }
    }
  }

  onRaiseIncident = (incident: Incident) => {
    this.reload();
  }

  openExternalWebsite = (websiteUrl: string) => {
    window.open(`${websiteUrl}`, "_blank");
  }

  processDataExternalWebsites = (language?: string) => {
    this.externalWebsites = this.projectConfiguration && this.projectConfiguration.trainExternalWebsites && 
    this.projectConfiguration.trainExternalWebsites.map(website => {
      return {
        text: website.label.find(label => label.language === (language || this.translate.getDefaultLang()))?.text,
        url: website.url.replace('<official_id>',  this.currentTrain && this.currentTrain.officialId && this.currentTrain.officialId.toString() || 'INVALID_TRAIN_OFFICIAL_ID')
                        .replace('<internal_id>', this.currentTrain && this.currentTrain.trainId && this.currentTrain.trainId.toString() || 'INVALID_TRAIN_ID')
                        .replace('<train_name>', this.currentTrain && this.currentTrain.name && this.currentTrain.name.toString() || 'INVALID_TRAIN_NAME')
                        .replace('<current_service_id>', this.currentTrain && this.currentTrain.currentServiceId && this.currentTrain.currentServiceId.toString() || 'INVALID_TRAIN_CURRENT_SERVICE')
                        .replace('<next_service_id>', this.currentTrain && this.currentTrain.nextServiceId && this.currentTrain.nextServiceId.toString() || 'INVALID_TRAIN_NEXT_SERVICE')
      }
    }).map(website => {
      return {
        ...website,
        disabled: website.url.includes('INVALID') || null
      }
    }) ;
  }

  ngOnDestroy() {
    this.monitoredCurrentTrainSubscription.unsubscribe();
  }
}
